import React, { useContext, useEffect, useMemo, useState } from 'react';
import CardBox from '../../../CardBox';
import { Button, Empty, Select } from '@maxtropy/components';
import { DoubleRightOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import ReactEcharts from 'echarts-for-react';
import { getChartOption } from './chart';
import {
  apiV2MicrogridOverviewGetBsaEnergyPost,
  apiV2MicrogridOverviewGetBsaEnergyStatisticsPost,
  apiV2MicrogridOverviewGetConfigBsaListPost,
  V2MicrogridOverviewGetConfigBsaListPostResponse,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { ShareDataContext } from '@/pages/IndustrialGreenMicBigScreen';
import { keepTwoOrLine } from '@/pages/IndustrialGreenMicBigScreen/utils';
import { isNil } from 'lodash-es';
import { useRequest } from 'ahooks';

enum ModeType {
  Chart = 1,
  Analysis = 2,
}

type BsaItem = Exclude<V2MicrogridOverviewGetConfigBsaListPostResponse['list'], undefined>[number];

const BsaChargingAndDischarging = () => {
  const { microgridId } = useContext(ShareDataContext);
  const [mode, setMode] = useState<ModeType>(ModeType.Chart);
  const [currentBsaId, setCurrentBsaId] = useState<number>();

  const { data: bsaList } = useRequest(
    async () => {
      const res = await apiV2MicrogridOverviewGetConfigBsaListPost({
        id: microgridId,
      });
      return res.list ?? [];
    },
    {
      ready: !!microgridId,
      refreshDeps: [microgridId],
      pollingInterval: 1000 * 60 * 15,
    }
  );

  useEffect(() => {
    if (isNil(bsaList)) return;
    setCurrentBsaId(bsaList?.[0]?.bsaId);
  }, [bsaList]);

  const { data: analysisData } = useRequest(
    () => {
      return apiV2MicrogridOverviewGetBsaEnergyStatisticsPost({
        id: microgridId,
        bsaId: currentBsaId,
      });
    },
    {
      ready: !!currentBsaId,
      refreshDeps: [currentBsaId],
      pollingInterval: 1000 * 60 * 15,
    }
  );

  const { data: chartData } = useRequest(
    () => {
      return apiV2MicrogridOverviewGetBsaEnergyPost({
        id: microgridId,
        bsaId: currentBsaId,
      });
    },
    {
      ready: !!currentBsaId,
      refreshDeps: [currentBsaId],
      pollingInterval: 1000 * 60 * 15,
    }
  );

  // 查询图表
  const chartOption = useMemo(() => getChartOption(chartData), [chartData]);

  return (
    <CardBox
      title="储能充放电"
      extraContent={
        <Button
          type="link"
          style={{ padding: '0' }}
          onClick={() => {
            window.open(`${window.BSSANALYSIS ?? ''}/energy/nonEeconomical/analysis?bsaId=${currentBsaId}`, '_blank');
          }}
        >
          能耗详情
          <DoubleRightOutlined className={styles.translationStyles} />
        </Button>
      }
    >
      <div className={styles.optionRow}>
        <Select
          style={{ width: 240 }}
          placeholder="请选择"
          options={bsaList}
          value={currentBsaId}
          onChange={v => setCurrentBsaId(v)}
          fieldNames={{ label: 'bsaName', value: 'bsaId' }}
        />
        <div className={styles.btnList}>
          <div
            onClick={() => setMode(ModeType.Chart)}
            className={mode === ModeType.Chart ? styles.btnActive : styles.btn}
          >
            数据统计
          </div>
          <div
            onClick={() => setMode(ModeType.Analysis)}
            className={mode === ModeType.Analysis ? styles.btnActive : styles.btn}
          >
            数据分析
          </div>
        </div>
      </div>
      {ModeType.Chart === mode &&
        (isNil(chartData) ||
        (chartData?.chargeInfo?.length === 0 && chartData.disChargeInfo?.length === 0) ||
        isNil(currentBsaId) ? (
          <Empty style={{ marginTop: 30 }} />
        ) : (
          <ReactEcharts style={{ flex: 1, marginTop: 12 }} option={chartOption} notMerge lazyUpdate={false} />
        ))}
      {ModeType.Analysis === mode && (
        <div className={styles.analysisRow}>
          <div className={styles.dayBox}>
            <div className={styles.monthTitleBg}></div>
            <div className={styles.both}>
              <div className={styles.title}>充电量(kWh)</div>
              <div className={styles.summit}>尖：{keepTwoOrLine(analysisData?.monthData?.summitCharge)}</div>
              <div className={styles.peak}>峰：{keepTwoOrLine(analysisData?.monthData?.peakCharge)}</div>
              <div className={styles.plain}>平：{keepTwoOrLine(analysisData?.monthData?.plainCharge)}</div>
              <div className={styles.valley}>谷：{keepTwoOrLine(analysisData?.monthData?.valleyCharge)}</div>
            </div>
            <div className={styles.splitVerticalLine}></div>
            <div className={styles.both}>
              <div className={styles.title}>放电量(kWh)</div>
              <div className={styles.summit}>尖：{keepTwoOrLine(analysisData?.monthData?.summitDischarge)}</div>
              <div className={styles.peak}>峰：{keepTwoOrLine(analysisData?.monthData?.peakDischarge)}</div>
              <div className={styles.plain}>平：{keepTwoOrLine(analysisData?.monthData?.plainDischarge)}</div>
              <div className={styles.valley}>谷：{keepTwoOrLine(analysisData?.monthData?.valleyDischarge)}</div>
            </div>
          </div>
          <div className={styles.dayBox}>
            <div className={styles.dayTitleBg}></div>
            <div className={styles.both}>
              <div className={styles.title}>充电量(kWh)</div>
              <div className={styles.summit}>尖：{keepTwoOrLine(analysisData?.lastDayData?.summitCharge)}</div>
              <div className={styles.peak}>峰：{keepTwoOrLine(analysisData?.lastDayData?.peakCharge)}</div>
              <div className={styles.plain}>平：{keepTwoOrLine(analysisData?.lastDayData?.plainCharge)}</div>
              <div className={styles.valley}>谷：{keepTwoOrLine(analysisData?.lastDayData?.valleyCharge)}</div>
            </div>
            <div className={styles.splitVerticalLine}></div>
            <div className={styles.both}>
              <div className={styles.title}>放电量(kWh)</div>
              <div className={styles.summit}>尖：{keepTwoOrLine(analysisData?.lastDayData?.summitDischarge)}</div>
              <div className={styles.peak}>峰：{keepTwoOrLine(analysisData?.lastDayData?.peakDischarge)}</div>
              <div className={styles.plain}>平：{keepTwoOrLine(analysisData?.lastDayData?.plainDischarge)}</div>
              <div className={styles.valley}>谷：{keepTwoOrLine(analysisData?.lastDayData?.valleyDischarge)}</div>
            </div>
          </div>
        </div>
      )}
    </CardBox>
  );
};

export default BsaChargingAndDischarging;
